'use client'

import { Builder } from '@builder.io/react'
import { type Input } from '@builder.io/sdk'

import { ButtonVariants } from '@src/components/Button/Button'
import { configPublic } from '@src/config.public'

import { SKUSelectorV2 } from './SKUSelectorV2'
import { SKUSelectorV2Defaults } from './SKUSelectorV2.defaults'

const shopifyOptionSubFields: Input[] = [
  {
    name: 'variantId',
    friendlyName: 'Shopify Variant ID (QA/Default)',
    type: 'text',
    required: configPublic.featureFlags.shopifyEnabled,
    helperText: 'Used for QA and as fallback for other environments',
  },
  {
    name: 'subscriptionPlanId',
    friendlyName: 'Subscription Plan ID (QA/Default)',
    type: 'text',
    helperText: 'External Plan ID from Recharge - Used for QA and as fallback',
  },
  {
    name: 'variantIdStage',
    friendlyName: 'Shopify Variant ID (Stage)',
    type: 'text',
    helperText: 'Stage environment variant ID',
  },
  {
    name: 'subscriptionPlanIdStage',
    friendlyName: 'Subscription Plan ID (Stage)',
    type: 'text',
    helperText: 'Stage environment subscription plan ID',
  },
  {
    name: 'variantIdProduction',
    friendlyName: 'Shopify Variant ID (Production)',
    type: 'text',
    helperText: 'Production environment variant ID',
  },
  {
    name: 'subscriptionPlanIdProduction',
    friendlyName: 'Subscription Plan ID (Production)',
    type: 'text',
    helperText: 'Production environment subscription plan ID',
  },
]

Builder.registerComponent(SKUSelectorV2, {
  name: 'SKUSelectorV2',
  inputs: [
    {
      name: 'previewAllItems',
      type: 'boolean',
      defaultValue: SKUSelectorV2Defaults.previewAllItems,
    },
    {
      name: 'style',
      type: 'string',
      enum: ['dropdown', 'radio', 'pill'],
      defaultValue: SKUSelectorV2Defaults.style,
    },
    {
      name: 'pill',
      type: 'object',
      showIf: 'options.get("style") === "pill"',
      defaultValue: SKUSelectorV2Defaults.pill,
      subFields: [
        {
          name: 'options',
          type: 'list',
          subFields: [
            {
              name: 'sku',
              type: 'text',
              localized: true,
            },
            {
              name: 'content',
              type: 'blocks',
              localized: true,
              hideFromUI: true,
            },
            ...shopifyOptionSubFields,
          ],
        },
      ],
    },
    {
      name: 'dropdown',
      type: 'object',
      showIf: 'options.get("style") === "dropdown"',
      defaultValue: SKUSelectorV2Defaults.dropdown,
      subFields: [
        {
          name: 'options',
          type: 'list',
          subFields: [
            {
              name: 'sku',
              type: 'text',
              localized: true,
            },
            {
              name: 'content',
              type: 'blocks',
              localized: true,
              hideFromUI: true,
            },
            {
              name: 'contentTop',
              type: 'blocks',
              localized: true,
              hideFromUI: true,
            },
            ...shopifyOptionSubFields,
          ],
        },
      ],
    },
    {
      name: 'radio',
      type: 'object',
      showIf: 'options.get("style") === "radio"',
      defaultValue: SKUSelectorV2Defaults.radio,
      subFields: [
        {
          name: 'options',
          type: 'list',
          subFields: [
            {
              name: 'isDropdown',
              type: 'boolean',
              defaultValue: false,
            },
            {
              name: 'sku',
              type: 'text',
              localized: true,
              showIf: 'options.get("isDropdown") !== true',
            },
            {
              name: 'content',
              type: 'blocks',
              localized: true,
              hideFromUI: true,
              defaultValue: SKUSelectorV2Defaults.radio?.options[1].content,
            },
            ...shopifyOptionSubFields.map((field) => ({
              ...field,
              showIf: 'options.get("isDropdown") !== true',
            })),
            {
              name: 'dropdownOptions',
              showIf: 'options.get("isDropdown") === true',
              type: 'list',
              defaultValue: SKUSelectorV2Defaults.radio?.options[0].dropdownOptions,
              subFields: [
                {
                  name: 'sku',
                  type: 'text',
                  localized: true,
                },
                {
                  name: 'content',
                  type: 'blocks',
                  localized: true,
                  hideFromUI: true,
                },
                {
                  name: 'contentTop',
                  type: 'blocks',
                  localized: true,
                  hideFromUI: true,
                },
                ...shopifyOptionSubFields,
              ],
            },
          ],
        },
      ],
    },
    {
      name: 'addToCartContent',
      type: 'blocks',
      hideFromUI: true,
      defaultValue: SKUSelectorV2Defaults.addToCartContent,
    },
    {
      name: 'addToCartVariant',
      type: 'string',
      enum: ButtonVariants,
      defaultValue: SKUSelectorV2Defaults.addToCartVariant,
      friendlyName: 'CTA Color Variant',
    },
    {
      name: 'redirectUrl',
      type: 'text',
      localized: true,
      defaultValue: SKUSelectorV2Defaults.redirectUrl,
    },
    {
      name: 'shopifyRedirectUrl',
      friendlyName: 'Shopify Redirect URL',
      type: 'text',
      helperText: 'URL to navigate to after adding to Shopify cart (optional)',
    },
  ],
})
